/**
 * DOM side of Tanda Mulai: the first-install bar and the settle. The mark FLIP-settles onto
 * `[data-mark-target]` (the header mark, or the login mark on /masuk) in 300 ms quint-out
 * with the Web Animations API, transform and opacity only; the veil, tick and text fade
 * through splash.css. Reduced motion, a missing target or a second tab: fade only.
 */
import type { SnapshotProgress } from '../../db/protocol'
import {
  type GateReason,
  type GateResult,
  installPercent,
  SPLASH_MEASURE,
  SPLASH_RELEASE_MARK,
  SPLASH_START_MARK,
} from './gate'

/** Switch the splash to the first-install bar and move it with each snapshot page. */
export function updateSplashInstall(progress: SnapshotProgress): void {
  const splash = document.getElementById('splash')
  if (splash === null) return
  splash.dataset.mode = 'unduhan'
  const percent = installPercent(progress)
  const bar = splash.querySelector<HTMLElement>('.bar')
  const label = splash.querySelector<HTMLElement>('.persen')
  if (bar !== null) {
    bar.style.setProperty('--p', percent === null ? '0' : String(percent / 100))
    if (percent === null) bar.removeAttribute('aria-valuenow')
    else bar.setAttribute('aria-valuenow', String(percent))
  }
  if (label !== null) label.textContent = percent === null ? '' : `${percent} %`
}

export interface SettleDetail {
  reason: GateReason
  flip: boolean
  from?: DOMRect
  to?: DOMRect
}

/** The mark FLIP-settles onto `[data-mark-target]` in 300 ms quint-out; everything else fades. */
export function settleSplash(result: GateResult): SettleDetail {
  const splash = document.getElementById('splash')
  const detail: SettleDetail = { reason: result.reason, flip: false }
  if (splash === null) return detail

  const mark = splash.querySelector<HTMLElement>('.mark')
  const target = document.querySelector<HTMLElement>('[data-mark-target]')
  const reduced =
    document.documentElement.dataset.motion === 'reduced' ||
    matchMedia('(prefers-reduced-motion: reduce)').matches

  let animation: Animation | null = null
  if (mark !== null && target !== null && !reduced && typeof mark.animate === 'function') {
    const from = mark.getBoundingClientRect()
    const to = target.getBoundingClientRect()
    if (from.width > 0 && to.width > 0) {
      const dx = to.left - from.left
      const dy = to.top - from.top
      const scale = to.width / from.width
      const end = `translate(${dx}px, ${dy}px) scale(${scale})`
      animation = mark.animate(
        [
          { transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
          { transform: end, opacity: 1, offset: 0.7 },
          { transform: end, opacity: 0, offset: 1 },
        ],
        { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' },
      )
      detail.flip = true
      detail.from = from
      detail.to = to
    }
  }

  splash.dataset.flip = String(detail.flip)
  splash.dataset.state = 'done'
  const remove = () => splash.remove()
  if (animation !== null) animation.finished.then(remove, remove)
  else setTimeout(remove, 260)

  performance.mark('satu:splash-settle', { detail })
  try {
    performance.measure(SPLASH_MEASURE, SPLASH_START_MARK, SPLASH_RELEASE_MARK)
  } catch {
    // marks missing (tests, very old browsers): the measure is diagnostic only
  }
  return detail
}
